import React, { useEffect, useMemo } from "react";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  PaymentMethodDescriptor,
  PaymentProvider,
  PaymentSelectionKey,
  toPaymentSelectionKey,
} from "@/services/student/checkout/checkout.interfaces";
import { usePaymentMethods } from "../payments/hooks/usePaymentMethods";
import { useCheckoutContext } from "../CheckoutContext";
import { formatRecurrenceIntervalLong } from "@/lib/recurrenceLabels";

interface PaymentMethodSelectionProps {
  selectedPaymentKey: PaymentSelectionKey | null;
  setPaymentMethod: (key: PaymentSelectionKey) => void;
  disabled?: boolean;
}

const PROVIDER_BADGE: Partial<Record<PaymentProvider, string>> = {
  MercadoPago: "Mercado Pago",
  Stripe: "Stripe",
  Paypal: "PayPal",
};

const isPixMethod = (descriptor: PaymentMethodDescriptor) =>
  descriptor.method.toLowerCase() === "pix";

const isMercadoPagoCard = (descriptor: PaymentMethodDescriptor) =>
  descriptor.provider === "MercadoPago" &&
  descriptor.method.toLowerCase() === "card";

export const PaymentMethodSelection: React.FC<PaymentMethodSelectionProps> = ({
  selectedPaymentKey,
  setPaymentMethod,
  disabled = false,
}) => {
  const { packageInfo } = useCheckoutContext();
  const { data: methods, isLoading, isError, refetch } = usePaymentMethods();
  const isRecurring = !!packageInfo?.isRecurring;

  const visibleMethods = useMemo(() => {
    if (!methods) return [];
    if (isRecurring) return methods.filter(isMercadoPagoCard);
    return methods;
  }, [methods, isRecurring]);

  useEffect(() => {
    if (!isRecurring || visibleMethods.length !== 1) return;
    const only = visibleMethods[0];
    const key = toPaymentSelectionKey(only.provider, only.method);
    if (selectedPaymentKey !== key) setPaymentMethod(key);
  }, [isRecurring, visibleMethods, selectedPaymentKey, setPaymentMethod]);

  const pixMethods = visibleMethods.filter(isPixMethod);
  const otherMethods = visibleMethods.filter((m) => !isPixMethod(m));

  const selectedIsOther =
    selectedPaymentKey &&
    otherMethods.some(
      (m) => toPaymentSelectionKey(m.provider, m.method) === selectedPaymentKey
    );

  return (
    <section
      id="payment-method-selection"
      className="rounded-xl border border bg-background"
      aria-labelledby="payment-method-heading"
    >
      <div className="border-b border px-6 py-5">
        <h3 id="payment-method-heading" className="text-lg font-semibold text-foreground">
          Forma de pagamento
        </h3>
        <p className="mt-1 text-sm text-text-secondary">
          {isRecurring
            ? `Assinatura ${formatRecurrenceIntervalLong(packageInfo?.recurrenceIntervalMonths)}: apenas cartão Mercado Pago.`
            : pixMethods.length > 0
              ? "PIX confirma em segundos na maioria dos bancos. Cartão e PayPal ficam em outras opções."
              : "Cartão e PayPal. O formulário seguro abre depois que você confirmar."}
        </p>
      </div>
      <div className="px-6 py-5">
        {isLoading && (
          <div className="flex items-center gap-2 text-text-secondary">
            <LoadingSpinner size="sm" />
            <span>Buscando formas de pagamento...</span>
          </div>
        )}

        {isError && (
          <div className="space-y-3">
            <p className="text-sm text-destructive">
              Não carregamos as formas de pagamento. Atualize a página ou tente de novo em alguns
              segundos.
            </p>
            <button
              onClick={() => void refetch()}
              className="text-sm font-medium text-foreground hover:text-foreground/80 underline"
              type="button"
            >
              Tentar novamente
            </button>
          </div>
        )}

        {!isLoading && !isError && visibleMethods.length === 0 && (
          <p className="text-sm text-text-secondary">
            Nenhuma forma de pagamento disponível agora. Tente outra vez em instantes.
          </p>
        )}

        {visibleMethods.length > 0 && (
          <div className="space-y-4">
            {pixMethods.length > 0 ? (
              <section aria-label="PIX">
                <p className="mb-3 text-sm font-medium text-success-subtle">
                  Recomendado
                </p>
                <div className="space-y-3 rounded-xl border-2 border-success bg-success-muted/30 p-3">
                  {pixMethods.map((descriptor) => (
                    <PaymentMethodOption
                      key={toPaymentSelectionKey(descriptor.provider, descriptor.method)}
                      descriptor={descriptor}
                      selectedPaymentKey={selectedPaymentKey}
                      setPaymentMethod={setPaymentMethod}
                      disabled={disabled}
                      highlighted
                    />
                  ))}
                </div>
              </section>
            ) : null}

            {otherMethods.length > 0 && pixMethods.length > 0 ? (
              <Accordion
                type="single"
                collapsible
                defaultValue={selectedIsOther ? "other-methods" : undefined}
              >
                <AccordionItem value="other-methods" className="border rounded-lg px-3">
                  <AccordionTrigger className="text-sm font-medium hover:no-underline py-4">
                    Outras formas de pagamento
                    <span className="ml-2 text-xs font-normal text-text-tertiary">
                      ({otherMethods.length})
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3 pb-2">
                      {otherMethods.map((descriptor) => (
                        <PaymentMethodOption
                          key={toPaymentSelectionKey(descriptor.provider, descriptor.method)}
                          descriptor={descriptor}
                          selectedPaymentKey={selectedPaymentKey}
                          setPaymentMethod={setPaymentMethod}
                          disabled={disabled}
                        />
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            ) : null}

            {otherMethods.length > 0 && pixMethods.length === 0 ? (
              <div className="space-y-3">
                {otherMethods.map((descriptor) => (
                  <PaymentMethodOption
                    key={toPaymentSelectionKey(descriptor.provider, descriptor.method)}
                    descriptor={descriptor}
                    selectedPaymentKey={selectedPaymentKey}
                    setPaymentMethod={setPaymentMethod}
                    disabled={disabled}
                  />
                ))}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </section>
  );
};

const PaymentMethodOption: React.FC<{
  descriptor: PaymentMethodDescriptor;
  selectedPaymentKey: PaymentSelectionKey | null;
  setPaymentMethod: (key: PaymentSelectionKey) => void;
  disabled: boolean;
  highlighted?: boolean;
}> = ({ descriptor, selectedPaymentKey, setPaymentMethod, disabled, highlighted = false }) => {
  const key = toPaymentSelectionKey(descriptor.provider, descriptor.method);
  const inputId = `payment-${key}`;
  const isSelected = selectedPaymentKey === key;
  const providerLabel = PROVIDER_BADGE[descriptor.provider] ?? descriptor.provider;

  return (
    <label
      htmlFor={inputId}
      className={`flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${
        isSelected
          ? "border-success-subtle bg-success-muted/50 ring-1 ring-success-subtle"
          : highlighted
            ? "border-success bg-background hover:border-success"
            : "border hover:border-light bg-background"
      } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
    >
      <input
        type="radio"
        id={inputId}
        name="payment-method"
        value={key}
        checked={isSelected}
        onChange={() => setPaymentMethod(key)}
        disabled={disabled}
        className="h-4 w-4 mt-1 shrink-0 text-success"
      />
      <div className="flex flex-col gap-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-foreground">{descriptor.displayName}</span>
          <span className="text-xs font-medium text-text-secondary bg-surface-subtle px-2 py-0.5 rounded-full">
            {providerLabel}
          </span>
        </div>
        {descriptor.description ? (
          <span className="text-sm text-text-secondary">{descriptor.description}</span>
        ) : null}
      </div>
    </label>
  );
};
